import { useEffect, useState } from 'react'

import {
  useGetCategoryNamesFromIdsQuery,
  useLazyCategoriesQuery,
} from '../../../../store/api'
import type {
  MetaResponse,
  PaginatedQueryParams,
} from '../../../../store/types'
import type { Category } from '../../../../store/types/category'
import type { DropdownOption } from '../../types'
import AsyncPaginatedDropdown from '../async-paginate'
import CreatebleAsyncPaginatedDropdown from '../createable-async-paginate'

type CategoriesDropdownProps<B extends boolean> = {
  name?: string
  onChange?: B extends true
    ? (item: DropdownOption[]) => void
    : (item: DropdownOption) => void
  isMulti?: B
  isClearable?: boolean
  isCreatable?: boolean
  exclude?: number
  value?: DropdownOption
  styles?: any
  components?: any
  filterValues?: any
  extendedStyles?: boolean
}

const categoriesToDropdownOptions = (category: Category): DropdownOption => ({
  label: category.name,
  value: category.id.toString(),
})

export default function CategoriesDropdown({
  name,
  onChange,
  isMulti = false,
  isClearable = false,
  isCreatable = false,
  exclude = undefined,
  value,
  styles,
  components,
  filterValues,
  extendedStyles,
}: CategoriesDropdownProps<boolean>) {
  const [fetcher] = useLazyCategoriesQuery()
  const categoriesFetcher = async (params: PaginatedQueryParams) => {
    const res = await fetcher({
      ...params,
      orderBy: 'name',
      orderDirection: 'asc',
    })
    const response: { data?: DropdownOption[]; meta?: MetaResponse<Category> } =
      {}
    if (exclude !== undefined) {
      const filteredResponse: any = res.data?.data
        .filter((category: any) => category?.id !== exclude)
        .map(categoriesToDropdownOptions)
      response.data = filteredResponse
    } else {
      response.data = res.data?.data.map(categoriesToDropdownOptions)
    }

    response.meta = res.data?.meta

    return response
  }

  const [defaultCategoryValues, setDefaultCategoryValues] = useState<
    Array<{ label: string; value: string }>
  >([])

  const { data: categoryNames } = useGetCategoryNamesFromIdsQuery(
    { ids: filterValues },
    { skip: !filterValues },
  )

  useEffect(() => {
    setDefaultCategoryValues(
      categoryNames?.map((item: any) => ({
        label: item?.name,
        value: item?.id.toString(),
      })) || [],
    )
  }, [categoryNames])

  let Element = AsyncPaginatedDropdown
  if (isCreatable) {
    Element = CreatebleAsyncPaginatedDropdown
  }

  return (
    <Element
      placeholder={
        isCreatable
          ? 'Type to create or select a category…'
          : 'Type to select a category…'
      }
      key={defaultCategoryValues[0]?.value}
      fetcher={categoriesFetcher}
      name={name}
      isMulti={isMulti}
      defaultValue={defaultCategoryValues}
      onChange={onChange}
      isClearable={isClearable}
      value={value}
      styles={styles}
      components={components}
      extendedStyles={extendedStyles}
    />
  )
}
